import type { VariantProps } from 'class-variance-authority';
import { useQueryClient } from '@tanstack/react-query';
import { cva } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import Toast from '@/components/ui/Toast/toast';
import { useDislikeGame } from '@/queries/games/game.mutations';
import { gamesApi } from '@/queries/games/games.api';
import { QueriesKeys } from '@/queries/queriesKeys';
import { useAppSelector } from '@/redux/hooks';
import { useIsGameFavorites } from '../hooks/isGameFavorited';

const gameLikeButtonVariants = cva('absolute z-2', {
  variants: {
    position: {
      default: 'top-3.5 right-3.5',
      compact: 'top-[0.375rem] right-[0.375rem]',
      compactLg: 'top-2.5 right-2 lg:top-3 lg:right-2',
    },
  },
  defaultVariants: {
    position: 'default',
  },
});

export type GameLikeButtonPosition = NonNullable<VariantProps<typeof gameLikeButtonVariants>['position']>;

const gameLikeButtonIconVariants = cva('text-white', {
  variants: {
    size: {
      default: 'size-5',
      compact: 'size-[1.125rem]',
      compactLg: 'size-6',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export type GameLikeButtonIconSize = NonNullable<VariantProps<typeof gameLikeButtonIconVariants>['size']>;

interface GameLikeButtonProps {
  gameId: string;
  iconSize?: GameLikeButtonIconSize;
  position?: GameLikeButtonPosition;
  isFavorited?: boolean;
}

const GameLikeButton = ({ gameId, iconSize = 'default', position = 'default', isFavorited: isExternalFavorite }: GameLikeButtonProps) => {
  const queryClient = useQueryClient();
  const userSession = useAppSelector(state => state.user.userSession);
  const { isInternalFavorite } = useIsGameFavorites(gameId);
  const [isMounted, setIsMounted] = useState(false);

  const isFavorite = isExternalFavorite ?? isInternalFavorite;
  const [optimisticFavorite, setOptimisticFavorite] = useState<boolean>(isFavorite);
  const t = useTranslations('game.favorites-games');
  const dislikeGameMutation = useDislikeGame();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (userSession) {
      setOptimisticFavorite(isFavorite);
    }
  }, [isFavorite, userSession]);

  // Keep SSR and initial client render identical to avoid hydration mismatch
  if (!isMounted || !userSession) {
    return null;
  }

  const likeGame = () => {
    setOptimisticFavorite(true);

    Toast({
      type: 'success',
      title: t('game-added-to-favorites-title'),
      description: t('game-added-to-favorites-text'),
    });

    gamesApi
      .likeGame(gameId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: [QueriesKeys.games, 'favorites'] });
      })
      .catch(() => {
        setOptimisticFavorite(isFavorite);
        Toast({ type: 'error', title: t('failed-to-add-game-to-favorites-title'), description: t('failed-to-add-game-to-favorites-text') });
      });
  };

  const dislikeGame = () => {
    setOptimisticFavorite(false);

    Toast({
      type: 'success',
      title: t('game-removed-from-favorites-title'),
      description: t('game-removed-from-favorites-text'),
    });

    dislikeGameMutation.mutate(gameId, {
      onError: () => {
        setOptimisticFavorite(isFavorite);
        Toast({
          type: 'error',
          title: t('failed-to-remove-game-from-favorites-title'),
          description: t('failed-to-remove-game-from-favorites-text'),
        });
      },
    });
  };

  return (
    <span className={gameLikeButtonVariants({ position })}>
      <button
        onClick={() => optimisticFavorite ? dislikeGame() : likeGame()}
        type="button"
        className="transition-transform duration-150 ease-out hover:scale-110"
      >
        <Icon name={optimisticFavorite ? 'liked' : 'unliked'} className={gameLikeButtonIconVariants({ size: iconSize })} />
      </button>
    </span>
  );
};

export default GameLikeButton;
